import type { HistoryEntry } from '../hooks/useHistory';

interface Props {
  entries: HistoryEntry[];
  onLoad: (entry: HistoryEntry) => void;
  onDelete: (id: string) => void;
  onClearAll: () => void;
  onClose: () => void;
}

function formatWhen(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (sameDay) return `Today, ${time}`;
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${time}`;
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1).trimEnd() + '…';
}

export function History({ entries, onLoad, onDelete, onClearAll, onClose }: Props) {
  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label="History">
      <div className="modal__overlay" onClick={onClose} />
      <div className="modal__panel">
        <div className="modal__header">
          <h2>History</h2>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Close history"
          >
            ×
          </button>
        </div>

        {entries.length === 0 ? (
          <p className="modal__hint">
            No saved prompts yet. Generate some ideas, then click{' '}
            <strong>Save to History</strong> on the results card.
          </p>
        ) : (
          <>
            <p className="modal__hint">
              Click an entry to load it back into the home page. Your
              last-used service is remembered separately.
            </p>
            <ul className="history">
              {entries.map((e) => {
                const picked = e.selectedNumber
                  ? e.options.find((o) => o.number === e.selectedNumber)?.domain
                  : null;
                return (
                  <li key={e.id} className="history-item">
                    <button
                      type="button"
                      className="history-item__main"
                      onClick={() => onLoad(e)}
                    >
                      <div className="history-item__when">
                        {formatWhen(e.savedAt)}
                      </div>
                      <div className="history-item__request">
                        {truncate(e.request, 120)}
                      </div>
                      <div className="history-item__meta">
                        {e.options.length} ideas
                        {picked ? (
                          <>
                            {' · picked #'}
                            {e.selectedNumber}: <strong>{picked}</strong>
                          </>
                        ) : null}
                      </div>
                    </button>
                    <button
                      type="button"
                      className="button button--ghost history-item__delete"
                      onClick={() => onDelete(e.id)}
                      aria-label={`Delete history entry from ${formatWhen(e.savedAt)}`}
                    >
                      Delete
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}

        <div className="modal__footer">
          {entries.length > 0 ? (
            <button
              type="button"
              className="button button--ghost"
              onClick={() => {
                if (confirm(`Clear all ${entries.length} history entries?`)) {
                  onClearAll();
                }
              }}
            >
              Clear all
            </button>
          ) : (
            <span />
          )}
          <button type="button" className="button button--primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
